/* Bringing a history of deposits and withdrawals in from a file.
 *
 * The reasoning about what may and may not be read out of one is in
 * `state/importCashFlows.ts`. This is the part that has to be looked at before
 * it is believed: what the file adds up to, what it will not count, and which
 * of the book's accounts each of its names is about — with the last of those
 * asked rather than assumed, because only the person who named the accounts
 * knows whether a file's "XTB_PLN" is this book's "XTB IKE".
 *
 * Rates are fetched per day, not per row. §5.1 converts a flow at the rate that
 * applied when it landed, deposits are entered long after the fact, and NBP
 * keeps its old tables — so a two-year history converts correctly instead of
 * being dragged to today's rate. A day with no table is written without one and
 * counted, rather than converted at something else and looking finished. */

import { useState } from 'react';
import * as api from '../api/client';
import { toUsd } from '../state/derive';
import {
  ratesNeeded, readCashFlows, toCashFlows, totalsByCurrency,
  guessAccount, type CashFlowImport,
} from '../state/importCashFlows';
import { useStore } from '../state/store';
import type { Account } from '../types';

export function ImportCashFlows({ say }: { say: (m: string) => void }) {
  const { state, putCashFlows } = useStore();
  const [text, setText] = useState('');
  const [read, setRead] = useState<CashFlowImport | null>(null);
  const [mapping, setMapping] = useState<Record<string, Account | ''>>({});
  const [busy, setBusy] = useState<string | null>(null);

  const accounts = state.data.accounts;

  const parse = (raw: string) => {
    const result = readCashFlows(raw);
    if (result.flows.length === 0) {
      setRead(null);
      return say(result.skipped.length
        ? `Nothing readable in it: ${result.skipped[0]!.why}.`
        : 'No deposits or withdrawals were found in that.');
    }
    setRead(result);
    setMapping(Object.fromEntries(
      result.sources.map((s) => [s, guessAccount(s, accounts) ?? '']),
    ));
  };

  const run = async () => {
    if (!read) return;
    const days = ratesNeeded(read.flows);
    const rates = new Map<string, number>();
    const currencies = [...new Set(read.flows.map((f) => f.currency))]
      .filter((c) => c !== 'USD' && c !== 'USDT');

    /* Several at a time rather than one after another.
     *
     * A two-year history touches fifty-odd days, and one request per day in
     * single file is half a minute of a screen that looks like it has hung. A
     * handful in flight at once turns that into a few seconds without asking
     * the Worker for anything it would not have been asked for anyway. A file
     * that carries its own rates needs none of this and the loop does not run. */
    let missed = 0;
    let done = 0;
    const LANES = 6;
    setBusy(days.length ? `Rates for ${days.length} days…` : 'Writing…');

    const queue = [...days];
    const lane = async () => {
      for (;;) {
        const day = queue.shift();
        if (day === undefined) return;
        try {
          const table = await api.fetchFxOn(day);
          for (const currency of currencies) {
            const rate = toUsd(1, currency, table);
            if (rate !== null) rates.set(`${day}|${currency}`, rate);
          }
        } catch {
          /* A day the table cannot be had for is left without a rate rather
             than given the wrong one. The book already renders that as
             converting at the current rate, and it is counted below so it is
             not a silence. */
          missed++;
        }
        done++;
        setBusy(`Rates · ${done} of ${days.length}…`);
      }
    };
    await Promise.all(Array.from({ length: Math.min(LANES, days.length) }, lane));

    setBusy('Writing…');
    try {
      const written = await putCashFlows(toCashFlows(read.flows, mapping, rates));
      const parts = [`${written} recorded`];
      if (read.transfers.length) parts.push(`${read.transfers.length} transfer(s) left out`);
      if (missed) parts.push(`${missed} day(s) without a rate`);
      say(`${parts.join(', ')}. Re-running the same file corrects them rather than doubling them.`);
      setRead(null);
      setText('');
    } catch (e) {
      say(`Could not write them: ${e instanceof Error ? e.message : 'unknown error'}`);
    } finally {
      setBusy(null);
    }
  };

  const totals = read ? totalsByCurrency(read.flows) : [];

  return (
    <>
      <div className="field">
        <label htmlFor="flow-paste">Paste a history, or choose a file</label>
        <textarea
          id="flow-paste" className="textarea" data-mono="true" spellCheck={false}
          placeholder={'date,time,account,type,currency,amount\n2026-04-18,02:41,REVOLUT,DEPOSIT,USD,2020.51'}
          value={text}
          onChange={(e) => { setText(e.target.value); setRead(null); }}
        />
        <div className="hint">
          Columns are read by their headings, in any order: <span className="mono">date</span>,{' '}
          <span className="mono">type</span>, <span className="mono">currency</span> and{' '}
          <span className="mono">amount</span> are needed;{' '}
          <span className="mono">time</span>, <span className="mono">account</span> and{' '}
          <span className="mono">note</span> are used when they are there. The type is{' '}
          <span className="mono">DEPOSIT</span>, <span className="mono">WITHDRAWAL</span> or{' '}
          <span className="mono">FX_SWAP</span>. The last of those is a transfer between your own
          accounts and is deliberately not counted as money paid in. A whole document can be pasted
          if the rows are in a <span className="mono">```csv</span> block. Add{' '}
          <span className="mono">rate_to_usd</span> and nothing has to be looked up: the import
          finishes at once instead of asking for one exchange rate per day.
        </div>
      </div>

      <div className="control-row">
        <input
          type="file" accept=".csv,.tsv,.txt,.md" id="flow-file" style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (!file) return;
            void file.text().then((raw) => { setText(raw); parse(raw); });
          }}
        />
        <button className="btn btn-sm" onClick={() => document.getElementById('flow-file')?.click()}>
          Choose a file
        </button>
        <button className="btn btn-sm btn-primary" disabled={text.trim() === ''}
                onClick={() => parse(text)}>
          Read it
        </button>
      </div>

      {read ? (
        <div className="card card-pad" style={{ marginTop: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>
            {read.flows.length} entries, before anything is written
          </div>

          <div className="kv-list" style={{ marginBottom: 12 }}>
            {totals.map((t) => (
              <div className="kv" key={t.currency}>
                <span className="dim">{t.currency}</span>
                <span className="num" style={{ marginLeft: 'auto' }}>
                  in {t.in.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  {t.out > 0 ? ` · out ${t.out.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ''}
                </span>
              </div>
            ))}
          </div>

          <div className="hint" style={{ marginBottom: 12 }}>
            Check these against the totals your own statement gives before importing. They are the
            file read back, not a figure this app worked out.
          </div>

          {read.sources.length > 0 ? (
            <>
              <div className="dim" style={{ fontSize: 11, letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 8 }}>
                Which account each name means
              </div>
              {read.sources.map((s) => (
                <div className="field" key={s}>
                  <label htmlFor={`map-${s}`}>{s}</label>
                  <select
                    id={`map-${s}`} className="select" value={mapping[s] ?? ''}
                    onChange={(e) => setMapping((m) => ({ ...m, [s]: e.target.value as Account | '' }))}
                  >
                    <option value="">Across the book</option>
                    {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </div>
              ))}
              <div className="hint" style={{ marginTop: -4, marginBottom: 12 }}>
                Left as <em>across the book</em> where the file's name matched nothing here, which
                is not a gap in the figures. Money paid in counts against the whole book and §5.1
                never asks which broker it went to, so the total is the same either way. Set one
                only where you know it.
              </div>
            </>
          ) : null}

          {read.transfers.length > 0 ? (
            <div className="hint" style={{ marginBottom: 10 }}>
              <strong>{read.transfers.length} transfer(s) will not be counted.</strong> Moving money
              between your own accounts (złoty into a dollar subaccount at the same broker) adds
              nothing to the book, and counting it would report funding you never did:{' '}
              {read.transfers.map((t) => t.what).join(' · ')}
            </div>
          ) : null}

          {read.skipped.length > 0 ? (
            <div className="hint hint-warn" style={{ marginBottom: 10 }}>
              {read.skipped.length} row(s) could not be read:{' '}
              {read.skipped.slice(0, 6).map((s) => `line ${s.line}, ${s.why}`).join(' · ')}
              {read.skipped.length > 6 ? ' …' : ''}
            </div>
          ) : null}

          <div className="fab-row" style={{ marginTop: 6 }}>
            <button className="btn btn-block" disabled={busy !== null}
                    onClick={() => setRead(null)}>Leave it</button>
            <button className="btn btn-primary btn-block" disabled={busy !== null}
                    onClick={() => void run()}>
              {busy ?? `Import ${read.flows.length}`}
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}
